import { useState } from 'react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import { Headphones, Loader2, X } from 'lucide-react';
import type { LiveCallListenResponse } from '@vagusx/clients';
import { Button } from '@/components/ui/button';

interface ListenInPanelProps {
  /** Connection details from POST /api/interactions/:id/listen. */
  session: LiveCallListenResponse;
  onStop: () => void;
}

/**
 * The supervisor's ear on a live call.
 *
 * AUDIO ONLY, AND ONE-WAY. `audio={false}` tells the SDK not to capture a
 * microphone, but that is the courtesy, not the guarantee — the token itself
 * carries `canPublish: false`, so nothing this component does (or that a
 * future edit to it does) can put the supervisor on the patient's line. The
 * page says so out loud because a person about to listen to a patient should
 * not have to infer it.
 *
 * `<RoomAudioRenderer />` is what actually plays the call: it attaches every
 * subscribed audio track to the page. Both legs — agent and caller — arrive
 * as separate tracks and mix in the browser.
 */
export function ListenInPanel({ session, onStop }: ListenInPanelProps): React.ReactElement {
  const [connected, setConnected] = useState(false);

  return (
    <LiveKitRoom
      token={session.token}
      serverUrl={session.livekitUrl}
      audio={false}
      video={false}
      connect={true}
      onConnected={() => setConnected(true)}
      onDisconnected={onStop}
      onError={(err) => {
        console.error('[ListenInPanel] LiveKit error:', err);
        onStop();
      }}
    >
      <RoomAudioRenderer />
      <div className="flex items-center gap-2 rounded-lg border bg-background p-2.5">
        {connected ? (
          <Headphones className="h-4 w-4 flex-shrink-0 text-success" />
        ) : (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />
        )}
        <div className="min-w-0 flex-grow">
          <p className="text-xs font-semibold">{connected ? 'Listening' : 'Connecting…'}</p>
          <p className="text-[11px] text-muted-foreground">They cannot hear you.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onStop} aria-label="Stop listening">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </LiveKitRoom>
  );
}
